import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Threat intelligence and incident correlation (part 89).
 *
 * The server owns normalisation, age decay and matching. This page only submits a feed, asks for a
 * correlation pass and renders what was stored, so the confidence shown beside a match is the
 * value the backend persisted — not a number recomputed in the browser that would drift from the
 * audit record.
 */

type Severity = 'low' | 'medium' | 'high' | 'critical'

interface Indicator {
  id: string
  kind: string
  kind_label: string
  value: string
  display_value?: string
  source: string
  confidence: number
  decayed_confidence: number
  age_days: number
  severity: Severity
  severity_label: string
  first_seen: string
  last_seen: string
  active: boolean
  tags: string[]
  match_count: number
}

interface MatchRow {
  id: string
  indicator_id: string
  indicator_kind: string
  indicator_kind_label: string
  indicator_value: string
  entity_kind: string
  entity_id: string
  entity_code: string
  entity_name: string
  matched_field: string
  field_label: string
  matched_value: string
  base_confidence: number
  confidence: number
  score: number
  severity: Severity
  severity_label: string
  status: 'open' | 'linked' | 'dismissed'
  incident_id?: string
  incident_title?: string
  incident_severity?: string
  first_matched_at: string
  last_matched_at: string
}

interface Feed {
  id: string
  source: string
  name: string
  feed_format: string
  indicator_count: number
  rejected_count: number
  imported_at: string
}

interface Rollup {
  total: number
  critical: number
  high: number
  medium: number
  low: number
  top_score: number
  average_score: number
  severity: Severity
}

interface RunResult {
  run_at: string
  indicators: number
  vendors: number
  employees: number
  created: number
  refreshed: number
  total: number
  rollup: Rollup
}

interface Rejection {
  line: number
  value: string
  reason: string
}

interface ImportResult {
  feed: Feed
  inserted: number
  updated: number
  rejected: number
  rejections: Rejection[]
}

interface IncidentRow {
  id: string
  title: string
  severity: string
  status: string
}

const severityTone: Record<Severity, string> = {
  low: 'sev-low',
  medium: 'sev-medium',
  high: 'sev-high',
  critical: 'sev-critical',
}

const statusLabels: Record<string, string> = {
  open: 'รอตรวจสอบ',
  linked: 'เชื่อมเหตุการณ์แล้ว',
  dismissed: 'ตัดออก',
}

const entityLabels: Record<string, string> = {
  vendor: 'ผู้ขาย',
  employee: 'พนักงาน',
}

const incidentSeverityLabels: Record<string, string> = {
  sev1: 'ระดับ 1',
  sev2: 'ระดับ 2',
  sev3: 'ระดับ 3',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function ThreatIntel() {
  const qc = useQueryClient()
  const [source, setSource] = useState('vendor-watch')
  const [name, setName] = useState('รายการเฝ้าระวังบัญชีผู้ขาย')
  const [raw, setRaw] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [linkTargets, setLinkTargets] = useState<Record<string, string>>({})

  const indicators = useQuery({
    queryKey: ['intel-indicators'],
    queryFn: () => list<Indicator>('/v1/threat-intel/indicators?limit=200'),
    staleTime: 30_000,
  })

  const matches = useQuery({
    queryKey: ['intel-matches'],
    queryFn: () => list<MatchRow>('/v1/threat-intel/matches?limit=200'),
    staleTime: 30_000,
  })

  const feeds = useQuery({
    queryKey: ['intel-feeds'],
    queryFn: () => list<Feed>('/v1/threat-intel/feeds?limit=20'),
    staleTime: 30_000,
  })

  const incidents = useQuery({
    queryKey: ['intel-incidents'],
    queryFn: () => list<IncidentRow>('/v1/incidents?status=open'),
    staleTime: 60_000,
  })

  const reload = () => {
    void qc.invalidateQueries({ queryKey: ['intel-indicators'] })
    void qc.invalidateQueries({ queryKey: ['intel-matches'] })
    void qc.invalidateQueries({ queryKey: ['intel-feeds'] })
  }

  const importFeed = useMutation({
    mutationFn: () =>
      apiWrite<ImportResult>('/v1/threat-intel/feeds', 'POST', {
        source,
        name,
        feed_format: 'csv',
        raw,
      }),
    onSuccess: (result) => {
      const rejected =
        result.rejected > 0 ? ` · ปฏิเสธ ${number(result.rejected)} รายการ` : ''
      setNotice(
        `นำเข้าจาก ${result.feed.source} สำเร็จ · ใหม่ ${number(result.inserted)} รายการ ` +
          `· อัปเดต ${number(result.updated)} รายการ${rejected}`,
      )
      setRaw('')
      reload()
    },
  })

  const runMatch = useMutation({
    mutationFn: () => apiWrite<RunResult>('/v1/threat-intel/matches/run', 'POST'),
    onSuccess: (result) => {
      setNotice(
        `ตรวจสอบ ${number(result.indicators)} ตัวบ่งชี้กับผู้ขาย ${number(result.vendors)} ราย ` +
          `และพนักงาน ${number(result.employees)} คน · พบใหม่ ${number(result.created)} รายการ ` +
          `· ปรับปรุง ${number(result.refreshed)} รายการ · คะแนนสูงสุด ${result.rollup.top_score}/100`,
      )
      reload()
    },
  })

  const linkMatch = useMutation({
    mutationFn: (args: { matchId: string; incidentId: string }) =>
      apiWrite<{ match: MatchRow }>(
        `/v1/threat-intel/matches/${args.matchId}/link`,
        'POST',
        { incident_id: args.incidentId },
      ),
    onSuccess: (result) => {
      setNotice(`เชื่อมโยงรายการกับเหตุการณ์ “${result.match.incident_title ?? ''}” แล้ว`)
      reload()
    },
  })

  const indicatorRows = indicators.data ?? []
  const matchRows = matches.data ?? []
  const feedRows = feeds.data ?? []
  const incidentRows = incidents.data ?? []

  const openMatches = matchRows.filter((row) => row.status !== 'dismissed')
  const urgent = openMatches.filter((row) => row.severity === 'high' || row.severity === 'critical')
  const topScore = openMatches.reduce((max, row) => Math.max(max, row.score), 0)
  const newestFeed = feedRows[0]

  const busy = importFeed.isPending || runMatch.isPending
  const mutationError = importFeed.error ?? runMatch.error ?? linkMatch.error

  return (
    <>
      <PageHead
        title="ข่าวกรองภัยคุกคาม"
        sub="นำเข้าตัวบ่งชี้ ICP จากแหล่งภายนอก แล้วจับคู่กับบัญชีรับเงินและเลขผู้เสียภาษีของผู้ขายและพนักงาน · ความเชื่อมั่นลดลงตามอายุของข้อมูล"
        actions={
          <button
            className="btn btn-primary btn-sm"
            disabled={runMatch.isPending}
            onClick={() => {
              setNotice(null)
              runMatch.mutate()
            }}
          >
            {runMatch.isPending ? 'กำลังจับคู่…' : 'จับคู่ใหม่'}
          </button>
        }
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 4, marginBottom: 16, padding: '10px 14px', fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)', borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      {mutationError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(mutationError, 'ดำเนินการไม่สำเร็จ')}
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat
          label="ตัวบ่งชี้ที่จัดเก็บ"
          value={number(indicatorRows.length)}
          unit="รายการ"
          foot={newestFeed ? `แหล่งล่าสุด ${newestFeed.source}` : 'ยังไม่มีการนำเข้า'}
        />
        <Stat
          label="รายการที่ต้องตรวจ"
          value={number(openMatches.length)}
          unit="รายการ"
          foot="ไม่นับรายการที่ตัดออกแล้ว"
        />
        <Stat
          label="ความเสี่ยงสูง"
          value={number(urgent.length)}
          unit="รายการ"
          foot="ระดับสูงและวิกฤตจากการจับคู่ล่าสุด"
        />
        <Stat
          label="คะแนนสูงสุด"
          value={topScore}
          unit="/ 100"
          foot="คิดจากความเชื่อมั่นที่ลดตามอายุและประเภทข้อมูลที่ตรง"
        />
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="stack">
          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">รายการเชื่อมโยงล่าสุด</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เรียงตามคะแนนความเสี่ยง
              </span>
            </div>

            {matches.isLoading ? (
              <div className="stack" style={{ marginTop: 12 }}><Skel h={160} /><Skel h={120} /></div>
            ) : matches.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>
                {errorText(matches.error, 'โหลดรายการเชื่อมโยงไม่สำเร็จ')}
              </div>
            ) : matchRows.length === 0 ? (
              <Empty
                title="ยังไม่พบรายการที่ตรงกับข้อมูลภายใน"
                body="นำเข้าตัวบ่งชี้แล้วกด “จับคู่ใหม่” ระบบจะเทียบบัญชีรับเงินและเลขผู้เสียภาษีของผู้ขายและพนักงานทั้งหมดในองค์กร"
              />
            ) : (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>นิติบุคคลหรือบุคคล</th>
                      <th>ตัวบ่งชี้</th>
                      <th>ข้อมูลที่ตรง</th>
                      <th style={{ textAlign: 'right' }}>คะแนน</th>
                      <th>เหตุการณ์</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchRows.map((row) => {
                      const target = linkTargets[row.id] ?? ''
                      return (
                        <tr key={row.id}>
                          <td>
                            <div>{row.entity_name}</div>
                            <div className="dim" style={{ fontSize: 11 }}>
                              {entityLabels[row.entity_kind] ?? row.entity_kind} · {row.entity_code}
                            </div>
                          </td>
                          <td>
                            <div className="tnum" style={{ fontSize: 12 }}>{row.indicator_value}</div>
                            <div className="dim" style={{ fontSize: 11 }}>
                              {row.indicator_kind_label} · ความเชื่อมั่น {row.base_confidence} → {row.confidence}
                            </div>
                          </td>
                          <td>
                            <div>{row.field_label}</div>
                            <div className="dim tnum" style={{ fontSize: 11 }}>{row.matched_value}</div>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <span className={`sev ${severityTone[row.severity]}`}>
                              <i className="sev-dot" />
                              {row.score}
                            </span>
                            <div className="dim" style={{ fontSize: 10.5 }}>{row.severity_label}</div>
                          </td>
                          <td>
                            {row.status === 'linked' ? (
                              <div style={{ fontSize: 12 }}>
                                <div>{row.incident_title || 'เชื่อมโยงแล้ว'}</div>
                                <div className="dim" style={{ fontSize: 11 }}>
                                  {incidentSeverityLabels[row.incident_severity ?? ''] ?? row.incident_severity ?? ''}
                                </div>
                              </div>
                            ) : (
                              <div className="row" style={{ gap: 6 }}>
                                <select
                                  className="select"
                                  aria-label={`เลือกเหตุการณ์สำหรับ ${row.entity_name}`}
                                  value={target}
                                  onChange={(e) =>
                                    setLinkTargets((prev) => ({ ...prev, [row.id]: e.target.value }))
                                  }
                                  style={{ minWidth: 150, height: 32 }}
                                >
                                  <option value="">เลือกเหตุการณ์</option>
                                  {incidentRows.map((incident) => (
                                    <option key={incident.id} value={incident.id}>
                                      {incident.title}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  className="btn btn-sm"
                                  disabled={!target || linkMatch.isPending}
                                  onClick={() => {
                                    setNotice(null)
                                    linkMatch.mutate({ matchId: row.id, incidentId: target })
                                  }}
                                >
                                  เชื่อมโยง
                                </button>
                              </div>
                            )}
                            <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                              {statusLabels[row.status] ?? row.status} · {thaiDateTime(row.last_matched_at)}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="card-label">ตัวบ่งชี้ที่จัดเก็บ</div>
            {indicators.isLoading ? (
              <div className="stack" style={{ marginTop: 12 }}><Skel h={140} /></div>
            ) : indicatorRows.length === 0 ? (
              <Empty
                title="ยังไม่มีตัวบ่งชี้"
                body="วางไฟล์ตัวบ่งชี้ในแบบฟอร์มด้านขวา โดยหนึ่งบรรทัดมีรูปแบบ kind,value,confidence,source"
              />
            ) : (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>ชนิด</th>
                      <th>ค่า</th>
                      <th style={{ textAlign: 'right' }}>ความเชื่อมั่น</th>
                      <th style={{ textAlign: 'right' }}>อายุ</th>
                      <th style={{ textAlign: 'right' }}>จับคู่</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indicatorRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.kind_label}</td>
                        <td>
                          <div className="tnum" style={{ fontSize: 12 }}>{row.display_value || row.value}</div>
                          <div className="dim" style={{ fontSize: 11 }}>{row.source || 'ไม่ระบุแหล่งที่มา'}</div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="tnum">{row.confidence} → {row.decayed_confidence}</div>
                          <div className="dim" style={{ fontSize: 10.5 }}>
                            <span className={`sev ${severityTone[row.severity]}`}>
                              <i className="sev-dot" />
                              {row.severity_label}
                            </span>
                          </div>
                        </td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{number(row.age_days)} วัน</td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{number(row.match_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="stack">
          <div className="card card-pad">
            <div className="card-label">นำเข้าตัวบ่งชี้</div>
            <div className="field" style={{ marginTop: 12 }}>
              <label htmlFor="intel-source">รหัสแหล่งข่าว</label>
              <input
                id="intel-source"
                className="input"
                value={source}
                maxLength={64}
                onChange={(e) => setSource(e.target.value)}
                placeholder="vendor-watch"
              />
            </div>
            <div className="field">
              <label htmlFor="intel-name">ชื่อชุดข้อมูล</label>
              <input
                id="intel-name"
                className="input"
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
                placeholder="รายการเฝ้าระวังบัญชีผู้ขาย"
              />
            </div>
            <div className="field">
              <label htmlFor="intel-raw">ตัวบ่งชี้ (CSV)</label>
              <textarea
                id="intel-raw"
                className="input"
                rows={7}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={'account,004-123-456-789,95,bank-watch\ndomain,evil.example.com,70,phish-feed'}
                style={{ width: '100%', height: 'auto', padding: '10px 12px', fontFamily: 'var(--font-mono, monospace)' }}
              />
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                หนึ่งบรรทัดต่อหนึ่งรายการ: kind,value,confidence,source · ชนิดที่รองรับ ip, domain, hash, account
              </div>
            </div>
            <button
              className="btn btn-primary"
              style={{ marginTop: 14, width: '100%' }}
              disabled={busy || raw.trim() === ''}
              onClick={() => {
                setNotice(null)
                importFeed.mutate()
              }}
            >
              {importFeed.isPending ? 'กำลังนำเข้า…' : 'นำเข้าตัวบ่งชี้'}
            </button>
            {importFeed.data && importFeed.data.rejections.length > 0 ? (
              <div style={{ marginTop: 12 }}>
                <div className="dim" style={{ fontSize: 11.5, marginBottom: 6 }}>
                  บรรทัดที่ถูกปฏิเสธ
                </div>
                {importFeed.data.rejections.slice(0, 8).map((rejection) => (
                  <div key={`${rejection.line}-${rejection.value}`} className="dim" style={{ fontSize: 11 }}>
                    บรรทัด {rejection.line}: {rejection.value} — {rejection.reason}
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="card card-pad">
            <div className="card-label">ประวัติการนำเข้า</div>
            {feeds.isLoading ? (
              <div className="stack" style={{ marginTop: 12 }}><Skel h={80} /></div>
            ) : feedRows.length === 0 ? (
              <div className="dim" style={{ marginTop: 10, fontSize: 12 }}>ยังไม่มีประวัติการนำเข้า</div>
            ) : (
              <div className="stack" style={{ marginTop: 10 }}>
                {feedRows.map((feed) => (
                  <div key={feed.id} className="row" style={{ alignItems: 'baseline', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12.5 }}>{feed.name}</div>
                      <div className="dim" style={{ fontSize: 11 }}>
                        {feed.source} · {thaiDateTime(feed.imported_at)}
                      </div>
                    </div>
                    <div className="dim tnum" style={{ marginLeft: 'auto', fontSize: 11, textAlign: 'right' }}>
                      <div>{number(feed.indicator_count)} รายการ</div>
                      {feed.rejected_count > 0 ? <div>ปฏิเสธ {number(feed.rejected_count)}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="card-label">สรุปตามระดับความรุนแรง</div>
            <div className="stack" style={{ marginTop: 10 }}>
              {(['critical', 'high', 'medium', 'low'] as Severity[]).map((band) => {
                const count = openMatches.filter((row) => row.severity === band).length
                return (
                  <div key={band} className="row" style={{ gap: 8 }}>
                    <span className={`sev ${severityTone[band]}`}>
                      <i className="sev-dot" />
                      {band === 'critical' ? 'วิกฤต' : band === 'high' ? 'สูง' : band === 'medium' ? 'ปานกลาง' : 'ต่ำ'}
                    </span>
                    <span className="tnum" style={{ marginLeft: 'auto' }}>{number(count)} รายการ</span>
                  </div>
                )
              })}
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 12 }}>
              ความเชื่อมั่นลดลงครึ่งหนึ่งทุก 30 วันนับจากวันที่พบล่าสุด
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
